import { defineStore } from 'pinia'
import { reactive } from 'vue'
import { triggerPaymentSuccessConfetti as triggerConfettiUtil } from '@/utils/confetti'

export const usePaymentStore = defineStore('payment', () => {
    const state = reactive({
        isProcessingPayment: false,
        showPaymentSuccessConfetti: false, // This can now be an internal trigger rather than watched externally
        paymentError: null,
        showBuyStarsModal: false,
        selectedPack: null,
        isCheckingPayment: false,
        currentTransactionId: '',
        paymentSuccess: false,
        paymentSuccessStars: 0,
        paymentFailed: false,
        failedTransactionId: '',
        failedPackId: null,
    })

    function setProcessingPayment(isLoading) {
        state.isProcessingPayment = isLoading
        if (isLoading) {
            state.paymentError = null // Clear previous errors when starting a new payment
        }
    }

    async function triggerPaymentSuccessConfetti() {
        // Use the utility function for consistent confetti behavior
        await triggerConfettiUtil()
    }

    function setPaymentError(error) {
        state.paymentError = error
        state.isProcessingPayment = false // Stop loading on error
    }

    function clearPaymentError() {
        state.paymentError = null
    }

    function showModal(pack = null) {
        state.selectedPack = pack
        state.showBuyStarsModal = true
    }

    function hideModal() {
        state.showBuyStarsModal = false
    }

    function setCheckingPayment(isChecking, transactionId = '') {
        state.isCheckingPayment = isChecking

        if (transactionId) {
            state.currentTransactionId = transactionId
        }

        // If starting a payment check, close the buy stars modal
        if (isChecking) {
            state.showBuyStarsModal = false
        }
    }

    function stopPaymentCheck() {
        state.isCheckingPayment = false
        state.currentTransactionId = ''
    }

    function setPaymentSuccess(success, stars = 0) {
        state.paymentSuccess = success
        state.paymentSuccessStars = stars

        // Clear failed state if success
        if (success) {
            state.paymentFailed = false
            state.failedTransactionId = ''
        }

        // Clear checking state
        if (success) {
            state.isCheckingPayment = false
        }
    }

    function setPaymentFailed(failed, transactionId = '', packId = null) {
        state.paymentFailed = failed
        state.failedTransactionId = transactionId
        state.failedPackId = packId

        // Clear success state if failed
        if (failed) {
            state.paymentSuccess = false
            state.paymentSuccessStars = 0
        }

        // Clear checking state
        if (failed) {
            state.isCheckingPayment = false
        }
    }

    function clearPaymentSuccess() {
        state.paymentSuccess = false
        state.paymentSuccessStars = 0
    }

    function clearPaymentFailed() {
        state.paymentFailed = false
        state.failedTransactionId = ''
        state.failedPackId = null
    }

    // Check if payment is in progress
    function isPaymentInProgress() {
        return state.isCheckingPayment
    }

    return {
        state,
        setProcessingPayment,
        triggerPaymentSuccessConfetti,
        setPaymentError,
        clearPaymentError,
        showModal,
        hideModal,
        setCheckingPayment,
        stopPaymentCheck,
        setPaymentSuccess,
        setPaymentFailed,
        clearPaymentSuccess,
        clearPaymentFailed,
        isPaymentInProgress,
    }
})
